import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BotidService } from '@shared-angular/services/ragai/botid.service';
import { downloadPdfSchema, downloadSkillIdSchema, downloadTextAsFileSchema } from '@shared/ragai.schemas';
import { firstValueFrom } from 'rxjs';
import { z } from 'zod';
import { BotService } from './bot.service';



/**
 * Ermöglicht den Download von Texten und Skill-Materialien (`RAGAI.DOWNLOAD`).
 * @remarks
 * {@link downloadMaterial}/{@link getMDContent} laden das **Original-Dokument** eines Skills;
 * {@link downloadFilledPDFTemplate}/{@link downloadFilledTemplate} füllen stattdessen eine Dokumentvorlage
 * serverseitig mit Platzhalterwerten und liefern das Ergebnis als neue Datei - unterschiedliche Backend-Endpunkte,
 * nicht nur unterschiedliche Parameter.
 */
@Injectable({
  providedIn: 'root'
})
export class DownloadManagerService {
  constructor(
    private botService: BotService,
    private botIdService: BotidService,
    private http: HttpClient
  ) { }

  private download(file: Blob, filename: string) {
    const fileURL = URL.createObjectURL(file);
    const element = document.createElement('a');
    element.setAttribute('href', fileURL);
    element.setAttribute('download', filename);
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(fileURL);
  }

  /**
   * Lädt einen Text als Datei herunter.
   * @param text Der herunterzuladende Text, oder ein Objekt gemäß `downloadTextAsFileSchema`.
   * @param filename Der Dateiname.
   */
  public textAsFile(text: string | z.input<typeof downloadTextAsFileSchema>, filename?: string): void {
    const parsed = downloadTextAsFileSchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      filename = parsed.data.filename;
      text = parsed.data.text;
    }
    const blob = new Blob([text as string], { type: 'text/plain' });
    this.download(blob, filename ?? '');
  }

  /**
   * Lädt das Original-Material eines Skills herunter.
   * @param skillId ID des Skills, oder ein Objekt gemäß `downloadSkillIdSchema`.
   * @returns Nichts.
   */
  public async downloadMaterial(skillId: string | z.input<typeof downloadSkillIdSchema>): Promise<void> {
    const parsed = downloadSkillIdSchema.safeParse(skillId);
    if (parsed.success && typeof skillId === 'object') {
      skillId = parsed.data.skillId;
    }
    const docInfo = await this.botService.getFileInformation(this.botIdService.getBotId(), skillId as string);
    const docContent = await this.botService.getDownloadableMaterial(this.botIdService.getBotId(), docInfo.id);
    const blob = new Blob([docContent], { type: docInfo.type });
    this.download(blob, docInfo.name);
  }

  /**
   * Lädt den Markdown-Inhalt eines Skill-Dokuments.
   * @param skillId ID des Skills, oder ein Objekt gemäß `downloadSkillIdSchema`.
   * @returns Metadaten und Markdown-Inhalt des Dokuments.
   */
  public async getMDContent(skillId: string | z.input<typeof downloadSkillIdSchema>): Promise<{ id: string, name: string, type: string, content: string }> {
    const parsed = downloadSkillIdSchema.safeParse(skillId);
    if (parsed.success && typeof skillId === 'object') {
      skillId = parsed.data.skillId;
    }
    return await this.botService.getMDFile(this.botIdService.getBotId(), skillId as string);
  }

  /**
   * Füllt eine PDF-Vorlage serverseitig aus und lädt sie herunter.
   * @param skillId ID des Vorlagen-Skills, oder ein Objekt gemäß `downloadPdfSchema`.
   * @param filename Der Dateiname für den Download.
   * @returns Nichts.
   */
  public async downloadFilledPDFTemplate(skillId: string | z.input<typeof downloadPdfSchema>, filename?: string): Promise<void> {
    const parsed = downloadPdfSchema.safeParse(skillId);
    if (parsed.success && typeof skillId === 'object') {
      filename = parsed.data.filename;
      skillId = parsed.data.skillId;
    }
    const res = await firstValueFrom(this.http.get("/api/user/docdownload/" + this.botIdService.getBotId() + "/filledtemplate/" + (skillId as string) + "/" + filename, { responseType: "blob" }));
    this.download(res, filename ?? '');
  }

  //die neue Funktion fuer die Neuen Blocklys
  /**
   * Füllt eine Dokumentvorlage mit Platzhalterwerten aus und lädt sie herunter.
   * @param skillId ID des Vorlagen-Skills, oder ein Objekt gemäß `downloadPdfSchema`.
   * @param filename Der Dateiname für den Download.
   * @param placeholder Platzhalter-Werte für die Vorlage.
   * @returns Nichts.
   */
  public async downloadFilledTemplate(skillId: string | z.input<typeof downloadPdfSchema>, filename: string, placeholder: object): Promise<void> {
    const parsed = downloadPdfSchema.safeParse(skillId);
    if (parsed.success && typeof skillId === 'object') {
      filename = parsed.data.filename;
      skillId = parsed.data.skillId;
    }
    const res = await firstValueFrom(this.http.post("/api/user/docdownload/" + this.botIdService.getBotId() + "/filledtemplate/" + (skillId as string) + "/" + filename, placeholder, { responseType: "blob" }));
    this.download(res, filename ?? '');
  }
}
